<template>
  <transition name="modal">
    <div class="modal-overlay">
      <div
        class="modal-wrapper showSweetAlert visible bulk-wrapper"
        data-animation="pop">

        <question v-if="displayQuestion"></question>

        <div class="modal-close col-sm-12 text-right">
          <button class="modal-button" @click="close">
            <span class="icon-close"></span>
          </button>
        </div>

        <header class="modal-header">
          <h2 class="modal-title">Transfer all notes to another account</h2>
        </header>

        <div class="modal-body mt-lg">

          <spinner v-if="loading" :text="loadingText" inline></spinner>

          <!-- ============ Preview could not be loaded ============ -->
          <!-- Distinct from the empty state on purpose: "no notes can be
               transferred" is an answer about the account, and showing it when
               we never got an answer reads as a fact about their notes. -->
          <div v-else-if="loadFailed">
            <div class="alert alert-danger" role="alert">
              <h4>We could not load this account's notes</h4>
              <p>{{ loadError }}</p>
            </div>

            <div class="modal-buttons mt-lg">
              <div class="row">
                <div class="col-md-12 text-right">
                  <button type="button" class="btn btn-info btn-lg btn-cancel" @click="close">
                    Close
                  </button>
                  <button
                    type="button"
                    class="btn btn-info btn-lg btn-accept"
                    @click="loadPreview">
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ============ Result summary ============ -->
          <div v-else-if="result">
            <div
              class="alert"
              :class="result.failed ? 'alert-warning' : 'alert-success'"
              role="alert">
              <h4 v-if="!result.failed">{{ result.title }}</h4>
              <h4 v-else>{{ result.title }} &mdash; with failures</h4>
              <p>{{ result.message }}</p>
            </div>

            <div v-if="result.failed" class="failures-block">
              <p class="text-muted">
                These notes were not processed and must be handled individually
                from each note's Transfer page:
              </p>
              <ul class="failure-list">
                <li v-for="failure in result.failures" :key="failure.noteId">
                  Note #{{ failure.noteId }}
                  <span v-if="failure.failedPhase">
                    ({{ failedPhaseText(failure.failedPhase) }})
                  </span>
                  &mdash; {{ failure.errorMessage }}
                </li>
              </ul>
            </div>

            <div class="modal-buttons mt-lg">
              <div class="row">
                <div class="col-md-12 text-right">
                  <button type="button" class="btn btn-info btn-lg btn-accept" @click="close">
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ============ Approval (second admin) ============ -->
          <div v-else-if="isApproval">
            <div class="alert alert-info" role="alert">
              <p>
                A bulk transfer of <strong>{{ openBatch.noteCount }}</strong> notes
                to account <strong>{{ destinationLabel }}</strong> is awaiting
                approval on this account.
              </p>
            </div>

            <!-- Counted from the batch, not a fresh assessment: the notes the
                 request named are excluded from reassessment precisely because
                 they are in flight, so the live figure here is always zero. -->
            <div v-if="openBatch.electionsToReset" class="alert alert-warning" role="alert">
              <p>
                {{ openBatch.electionsToReset }} upcoming transaction(s) are
                scheduled on these notes. Approving resets them, and anything
                the investor already chose must be chosen again on the
                destination account.
              </p>
            </div>

            <!-- Totals -->
            <div class="totals-block">
              <div class="row">
                <div class="col-md-6"><span class="totals-label">Notes to transfer</span></div>
                <div class="col-md-6 text-right">
                  <strong>{{ openBatch.noteCount }}</strong>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6"><span class="totals-label">Principal</span></div>
                <div class="col-md-6 text-right">
                  {{ formatAmount(openBatch.totalPrincipal || 0) }}
                </div>
              </div>
              <div class="row" v-if="openBatch.electionsToReset">
                <div class="col-md-6"><span class="totals-label">Upcoming transactions</span></div>
                <div class="col-md-6 text-right text-danger">
                  {{ openBatch.electionsToReset }}
                </div>
              </div>
            </div>

            <p class="toggle-link" v-if="requestNotes.length">
              <a href @click.prevent="showNotes = !showNotes">
                {{ showNotes ? 'Hide' : 'Show' }} the {{ requestNotes.length }} notes
                being transferred
              </a>
            </p>
            <div v-if="showNotes && requestNotes.length" class="note-list">
              <table class="table table-condensed">
                <thead>
                  <tr>
                    <th>Note</th>
                    <th class="text-right">Balance</th>
                    <th class="text-right">Upcoming</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="note in requestNotes" :key="note.noteId">
                    <td>{{ note.noteNumber }}</td>
                    <td class="text-right">{{ formatAmount(note.principalAmount) }}</td>
                    <td class="text-right">
                      <span v-if="note.electionsToReset" class="text-danger">
                        <em class="fa fa-warning pr-sm"></em>{{ note.electionsToReset }}
                      </span>
                      <span v-else class="text-muted">&mdash;</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- One box holding both comments, as the single-note flow does:
                 the requester's comment is already in it and the approver
                 appends. -->
            <fieldset class="form-group">
              <label for="bulkTransferApprovalComment">
                Please append your comment below *
              </label>
              <textarea
                id="bulkTransferApprovalComment"
                class="form-control"
                rows="4"
                maxlength="255"
                v-model.trim="approvalComment">
              </textarea>
              <p class="text-danger" v-if="!canApprove">
                <em>You must enter or append a comment.</em>
              </p>
            </fieldset>

            <div class="modal-buttons mt-lg">
              <div class="row">
                <div class="col-md-12 text-right">
                  <button type="button" class="btn btn-info btn-lg btn-cancel" @click="close">
                    Close
                  </button>
                  <button
                    type="button"
                    class="btn btn-info btn-lg btn-discard"
                    :disabled="!canApprove"
                    @click="confirmCancelBatch">
                    Cancel request
                  </button>
                  <button
                    type="button"
                    class="btn btn-info btn-lg btn-accept"
                    :disabled="!canApprove"
                    @click="confirmApproval">
                    Approve all
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ============ Request (first admin) ============ -->
          <div v-else>
            <div v-if="preview.eligibleCount === 0" class="alert alert-warning" role="alert">
              <p>
                No notes on this account can be transferred
                <span v-if="preview.ineligibleCount">
                  &mdash; all {{ preview.ineligibleCount }} of them are excluded.
                  See the reasons below.
                </span>
              </p>
            </div>

            <fieldset class="form-group">
              <label for="bulkTransferSearch">Destination account *</label>
              <form role="form" novalidate @submit.prevent="searchAccount">
                <div class="searchbox">
                  <input
                    id="bulkTransferSearch"
                    v-model="search.text"
                    type="text"
                    class="form-control"
                    placeholder="Search account number"
                    autocomplete="off" />
                  <button
                    type="submit"
                    class="btn btn-default btn-lg"
                    :disabled="!search.text || searching">
                    <em class="icon-magnifier pr-sm"></em> Search
                  </button>
                </div>
              </form>
            </fieldset>

            <div v-if="search.hasSearched && !search.account && !searching"
              class="alert alert-info not-found-alert" role="alert">
              <p><em class="icon-info pr-sm"></em>No results found.</p>
            </div>

            <table
              v-if="selectedAccount || search.account"
              class="table table-striped bank-accounts table-fundings">
              <thead>
                <tr>
                  <th>Account number</th>
                  <th>Account name</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="selectedAccount">
                  <td>{{ selectedAccount.accountId }}</td>
                  <td>{{ selectedAccount.accountName }}</td>
                  <td>
                    <span class="action-span green-text">
                      <i class="fa fa-check check"></i>Selected
                    </span>
                  </td>
                </tr>
                <tr v-if="search.account && !isSelected(search.account)">
                  <td>{{ search.account.accountId }}</td>
                  <td>{{ search.account.accountName }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-details btn-outline"
                      @click="selectAccount(search.account)">
                      Select account
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- ============ Warnings ============ -->
            <div v-if="preview.warnings.length" class="alert alert-warning warnings-block" role="alert">
              <h4>Before this account is migrated</h4>
              <ul class="warning-list">
                <li v-for="warning in preview.warnings" :key="warning.code">
                  {{ warning.text }}
                </li>
              </ul>
            </div>

            <!-- Closed notes are excluded by default, but the single-note
                 transfer flow has never gated on status, so bulk should not be
                 stricter than the page it replaces. -->
            <fieldset class="checkbox c-checkbox closed-notes-toggle">
              <label>
                <input
                  type="checkbox"
                  name="includeClosedNotes"
                  v-model="includeClosedNotes"
                  @change="loadPreview()">
                <span class="fa fa-check"></span>
                Include closed notes
              </label>
              <p class="text-muted closed-notes-hint">
                Closed notes carry no balance. Moving them keeps the account's
                history together rather than splitting it across two accounts.
              </p>
            </fieldset>

            <!-- ============ Totals ============ -->
            <div class="totals-block">
              <div class="row">
                <div class="col-md-6"><span class="totals-label">Notes to transfer</span></div>
                <div class="col-md-6 text-right">
                  <strong>{{ preview.eligibleCount }}</strong>
                  <span class="text-muted" v-if="preview.ineligibleCount">
                    ({{ preview.ineligibleCount }} excluded)
                  </span>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6"><span class="totals-label">Principal moving</span></div>
                <div class="col-md-6 text-right">
                  {{ formatAmount(preview.totals.principal) }}
                </div>
              </div>
              <div class="row" v-if="preview.electionsToReset">
                <div class="col-md-6">
                  <span class="totals-label">
                    Upcoming transactions
                  </span>
                </div>
                <div class="col-md-6 text-right text-danger">
                  {{ preview.electionsToReset }}
                </div>
              </div>
              <div class="row" v-if="preview.terminalNoteCount">
                <div class="col-md-6">
                  <span class="totals-label">Closed or cancelled notes staying here</span>
                </div>
                <div class="col-md-6 text-right">{{ preview.terminalNoteCount }}</div>
              </div>
            </div>

            <fieldset class="form-group">
              <label for="bulkTransferComment">Please append your comment *</label>
              <textarea
                id="bulkTransferComment"
                class="form-control"
                rows="2"
                maxlength="255"
                v-model.trim="comment"></textarea>
            </fieldset>

            <!-- ============ Note lists ============ -->
            <p class="toggle-link" v-if="preview.eligibleCount">
              <a href @click.prevent="showNotes = !showNotes">
                {{ showNotes ? 'Hide' : 'Show' }} the {{ preview.eligibleCount }} notes
                being transferred
              </a>
            </p>
            <div v-if="showNotes" class="note-list">
              <table class="table table-condensed">
                <thead>
                  <tr>
                    <th>Note</th>
                    <th class="text-right">Balance</th>
                    <th class="text-center">Maturity</th>
                    <th class="text-right">Upcoming</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="note in preview.eligibleNotes" :key="note.noteId">
                    <td>{{ note.noteNumber }}</td>
                    <td class="text-right">{{ formatAmount(note.currentBalance) }}</td>
                    <td class="text-center">{{ formatDate(note.maturityDate) }}</td>
                    <!-- A dash rather than 0 so the notes carrying something
                         scheduled stand out in a list of fifty. -->
                    <td class="text-right">
                      <span v-if="note.electionsToReset" class="text-danger">
                        <em class="fa fa-warning pr-sm"></em>{{ note.electionsToReset }}
                      </span>
                      <span v-else class="text-muted">&mdash;</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p class="toggle-link" v-if="preview.ineligibleCount">
              <a href @click.prevent="showExcluded = !showExcluded">
                {{ showExcluded ? 'Hide' : 'Show' }} the {{ preview.ineligibleCount }} notes
                that would stay
              </a>
            </p>
            <div v-if="showExcluded" class="note-list">
              <table class="table table-condensed">
                <thead>
                  <tr>
                    <th>Note</th>
                    <th class="text-right">Balance</th>
                    <th>Why it stays</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="note in preview.ineligibleNotes" :key="note.noteId">
                    <td>{{ note.noteNumber }}</td>
                    <td class="text-right">{{ formatAmount(note.currentBalance) }}</td>
                    <td class="text-muted">{{ note.ineligibilityText }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="modal-buttons mt-lg">
              <div class="row">
                <div class="col-md-12 text-right">
                  <button type="button" class="btn btn-info btn-lg btn-cancel" @click="close">
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-info btn-lg btn-accept"
                    :disabled="!canSubmit"
                    @click="confirmSubmit">
                    Transfer {{ preview.eligibleCount }} notes
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import { mapActions, mapMutations, mapState } from 'vuex';

  import {
    HIDE_QUESTION_MODAL,
    SET_ERRORS_MODAL_INFO,
    SHOW_ERRORS_MODAL,
    SHOW_QUESTION_MODAL,
  } from '@/store/mutation-types';
  import { formatAmount, formatDate } from '@/util';
  import { SearchTypes } from '@/data/static/searchTypes';
  import Question from '@/components/modals/Question';
  import spinner from '@/components/widgets/Spinner';

  /** How often the batch status is polled while a phase runs in the background. */
  const POLL_INTERVAL_MS = 2000;
  /** Stop polling after this long so a stalled batch cannot spin forever. */
  const POLL_TIMEOUT_MS = 10 * 60 * 1000;

  const PHASE_TEXT = {
    REQUEST: 'could not be requested',
    APPROVAL: 'request raised but not approved',
    CANCELLATION: 'request could not be reversed',
  };

  const emptyPreview = () => ({
    fromAccount: null,
    toAccount: null,
    eligibleCount: 0,
    ineligibleCount: 0,
    terminalNoteCount: 0,
    electionsToReset: 0,
    eligibleNotes: [],
    ineligibleNotes: [],
    warnings: [],
    totals: { principal: 0 },
    openBatch: null,
  });

  export default {
    name: 'bulk-transfer',
    components: {
      question: Question,
      spinner,
    },
    props: ['accountId'],
    data() {
      return {
        loading: true,
        loadingText: 'Loading…',
        loadFailed: false,
        loadError: '',
        searching: false,
        preview: emptyPreview(),
        selectedAccount: null,
        includeClosedNotes: false,
        comment: '',
        approvalComment: '',
        showNotes: false,
        showExcluded: false,
        result: null,
        pollTimer: null,
        search: {
          text: '',
          hasSearched: false,
          account: null,
        },
      };
    },
    beforeMount() {
      this.loadPreview();
    },
    beforeDestroy() {
      this.stopPolling();
      // The question modal lives in the store, so an open confirmation would
      // outlive this modal and strand the user behind it.
      this.hideQuestionModal();
    },
    computed: {
      ...mapState('question', { displayQuestion: 'display' }),
      openBatch() {
        return this.preview.openBatch || {};
      },
      /** A batch already awaiting approval turns this modal into the approve step. */
      isApproval() {
        return Boolean(
          this.preview.openBatch &&
            this.preview.openBatch.status === 'PENDING_APPROVAL',
        );
      },
      destinationLabel() {
        if (this.preview.toAccount) {
          return this.preview.toAccount.accountNumber;
        }
        return this.openBatch.toAccountId;
      },
      /** The notes the open request named, from its own snapshot. */
      requestNotes() {
        return this.openBatch.notes || [];
      },
      approvalMatchesRequest() {
        return Boolean(
          this.approvalComment &&
            this.approvalComment === this.openBatch.requestComment,
        );
      },
      canApprove() {
        return Boolean(this.approvalComment) && !this.approvalMatchesRequest;
      },
      canSubmit() {
        if (!this.preview.eligibleCount) return false;
        if (!this.selectedAccount) return false;
        if (!this.comment) return false;
        return true;
      },
    },
    methods: {
      formatAmount,
      formatDate,
      ...mapActions('note', [
        'previewBulkNoteTransfer',
        'createBulkNoteTransfer',
        'getBulkNoteTransfer',
        'approveBulkNoteTransfer',
        'cancelBulkNoteTransfer',
      ]),
      ...mapActions('account', ['getAccounts']),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('question', {
        showQuestionModal: SHOW_QUESTION_MODAL,
        hideQuestionModal: HIDE_QUESTION_MODAL,
      }),
      async loadPreview() {
        this.loading = true;
        this.loadingText = 'Loading…';
        this.loadFailed = false;

        try {
          const preview = await this.previewBulkNoteTransfer({
            accountId: this.accountId,
            toAccountId: this.selectedAccount ? this.selectedAccount.id : null,
            includeClosedNotes: this.includeClosedNotes,
          });
          this.preview = Object.assign(emptyPreview(), preview);
          this.seedApprovalComment();
        } catch (error) {
          this.loadError = this.errorText(
            error,
            'The request failed. The notes on this account have not been checked.',
          );
          this.loadFailed = true;
          this.fail(this.loadError);
        } finally {
          this.loading = false;
        }
      },
      /**
       * Puts the requester's comment in the approver's box, once.
       *
       * Only on the first load that finds a request awaiting approval: a later
       * reload must not overwrite what the approver has typed.
       */
      seedApprovalComment() {
        const batch = this.preview.openBatch;
        if (!batch || !batch.requestComment) return;
        if (this.approvalComment) return;

        this.approvalComment = batch.requestComment;
      },
      async searchAccount() {
        if (!this.search.text) return;

        this.searching = true;

        try {
          const response = await this.getAccounts({
            searchString: this.search.text,
            searchBy: SearchTypes.ACCOUNT_NUMBER.name,
          });
          this.search.hasSearched = true;
          this.search.account = response.statusCode === 401 ? null : response.account;
        } catch (error) {
          this.setErrorsModalInfo(['We could not retrieve the account.']);
          this.showErrorsModal();
        } finally {
          this.searching = false;
        }
      },
      isSelected(account) {
        return Boolean(this.selectedAccount && this.selectedAccount.id === account.id);
      },
      /**
       * Re-previews against the chosen destination. The note assessment does
       * not change, but the destination checks — inactive account, different
       * investor, nothing to pay out through — only exist once there is one.
       */
      selectAccount(account) {
        if (String(account.id) === String(this.accountId)) {
          this.setErrorsModalInfo(['Please select a different account.']);
          this.showErrorsModal();
          return;
        }

        this.selectedAccount = account;
        this.loadPreview();
      },
      confirmSubmit() {
        const destination =
          this.selectedAccount.accountId || this.selectedAccount.id;

        let question =
          `This transfers ${this.preview.eligibleCount} notes to account ` +
          `${destination}.`;

        if (this.includeClosedNotes) {
          question += ' Closed notes are included in that count.';
        }

        if (this.preview.electionsToReset) {
          question +=
            ` It resets ${this.preview.electionsToReset} upcoming ` +
            'transaction(s) once approved, and anything the investor already ' +
            'chose must be chosen again on the destination account.';
        }

        question += ' Another admin must approve it before any note moves.';

        this.askToConfirm({
          title: 'Request this bulk transfer?',
          question,
          yesButtonText: 'Request',
          method: this.submit,
        });
      },
      async submit() {
        this.loading = true;
        this.loadingText = 'Requesting transfers…';

        try {
          const { batchId } = await this.createBulkNoteTransfer({
            accountId: this.accountId,
            payload: {
              toAccountId: this.selectedAccount.id,
              comment: this.comment,
              includeClosedNotes: this.includeClosedNotes,
              noteIds: this.preview.eligibleNotes.map(note => note.noteId),
              expectedNoteCount: this.preview.eligibleCount,
            },
          });
          await this.pollUntilSettled(batchId, 'CREATING', 'Transfer requested');
        } catch (error) {
          this.fail(this.errorText(error, 'We could not submit the bulk transfer.'));
        }
      },
      confirmApproval() {
        this.askToConfirm({
          title: 'Approve this bulk transfer?',
          question:
            `This moves ${this.openBatch.noteCount} notes to account ` +
            `${this.destinationLabel}. It cannot be undone here — reversing ` +
            'it means transferring them back.',
          yesButtonText: 'Approve',
          method: this.approve,
        });
      },
      async approve() {
        this.loading = true;
        this.loadingText = 'Transferring notes…';

        try {
          await this.approveBulkNoteTransfer({
            accountId: this.accountId,
            batchId: this.openBatch.id,
            payload: { comment: this.approvalComment },
          });
          await this.pollUntilSettled(this.openBatch.id, 'APPROVING', 'Notes transferred');
        } catch (error) {
          this.fail(this.errorText(error, 'We could not approve the bulk transfer.'));
        }
      },
      confirmCancelBatch() {
        this.askToConfirm({
          title: 'Withdraw this bulk transfer?',
          question:
            `This withdraws every request raised for the ${this.openBatch.noteCount} ` +
            'notes in this batch. They stay where they are.',
          yesButtonText: 'Withdraw',
          method: this.cancelBatch,
        });
      },
      /**
       * Asks the shared question modal, then runs the action on confirm.
       *
       * The modal is the app's own confirmation dialog; window.confirm was a
       * placeholder and is blocked outright in some embedded contexts.
       */
      askToConfirm({ title, question, yesButtonText, method }) {
        this.showQuestionModal({
          title,
          question,
          yesButtonText,
          hideCancelButton: false,
          method: () => {
            this.hideQuestionModal();
            method();
          },
        });
      },
      async cancelBatch() {
        this.loading = true;
        this.loadingText = 'Withdrawing requests…';

        try {
          const { cancelledNotes } = await this.cancelBulkNoteTransfer({
            accountId: this.accountId,
            batchId: this.openBatch.id,
            payload: { comment: this.approvalComment },
          });
          this.loading = false;
          this.result = {
            title: 'Request cancelled',
            message: `${cancelledNotes} note transfer request(s) were withdrawn.`,
            failed: false,
            failures: [],
          };
          this.$emit('completed');
        } catch (error) {
          this.fail(this.errorText(error, 'We could not cancel the bulk transfer.'));
        }
      },
      /**
       * Both phases return 202 and finish in the background, so the modal polls
       * the batch until it leaves the in-progress status.
       */
      async pollUntilSettled(batchId, inProgressStatus, successTitle) {
        const startedAt = Date.now();

        const check = async () => {
          let batch;
          try {
            batch = await this.getBulkNoteTransfer({
              accountId: this.accountId,
              batchId,
            });
          } catch (error) {
            this.fail('We lost track of this batch. Reopen to see its status.');
            return;
          }

          if (batch.status === inProgressStatus) {
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
              this.fail(
                'This is taking longer than expected. Reopen the modal to check progress.',
              );
              return;
            }
            this.pollTimer = setTimeout(check, POLL_INTERVAL_MS);
            return;
          }

          this.stopPolling();
          this.loading = false;

          // Which count means "processed" depends on the phase: requesting
          // leaves notes awaiting approval, approving moves them.
          const done =
            inProgressStatus === 'CREATING'
              ? batch.progress.requested
              : batch.progress.transferred;
          const failedSuffix = batch.progress.failed
            ? ` ${batch.progress.failed} failed.`
            : '';

          this.result = {
            title: successTitle,
            message: `${done} of ${batch.noteCount} notes processed.${failedSuffix}`,
            failed: batch.progress.failed > 0,
            failures: batch.failures || [],
          };
          this.$emit('completed');
        };

        await check();
      },
      stopPolling() {
        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }
      },
      failedPhaseText(phase) {
        return PHASE_TEXT[phase] || phase;
      },
      errorText(error, fallback) {
        const message =
          error && error.response && error.response.data && error.response.data.message;
        return message || fallback;
      },
      fail(message) {
        this.stopPolling();
        this.loading = false;
        this.setErrorsModalInfo([message]);
        this.showErrorsModal();
      },
      close() {
        this.stopPolling();
        this.$emit('close');
      },
    },
  };
</script>

<style scoped>
  /* base.scss sets .modal-overlay/.modal-wrapper to display:none and caps the
     wrapper at 580px. Overridden the same way BulkRedemption.vue does; wider
     than the standard modal because the note tables need the room. */
  .modal-overlay {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow-y: auto;
    padding: 15px;
    padding-top: 80px;
    -webkit-overflow-scrolling: touch;
  }

  .modal-wrapper.bulk-wrapper {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    display: block;
    margin: auto;
    flex-shrink: 0;
    width: 90%;
    max-width: 820px;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .searchbox {
    display: flex;
    flex-direction: row;
  }
  .searchbox input {
    height: 39px;
    flex: 1;
    border-radius: 4px 0 0 4px;
  }
  .searchbox .btn-default {
    text-align: center;
    border-radius: 0 4px 4px 0;
  }

  .not-found-alert {
    margin-top: 10px;
  }

  .green-text {
    color: #558c1d;
    font-weight: 800;
  }
  .action-span {
    display: block;
    height: 32px;
    min-width: 113px;
    padding: 5px 0;
  }
  .check {
    margin-right: 5px;
  }

  .warnings-block {
    margin-top: 10px;
  }
  .warning-list {
    margin-bottom: 0;
    padding-left: 18px;
  }

  /* Matches the summary panel used on the bulk redemption modal. */
  .totals-block {
    background: #f7f9fa;
    border-radius: 4px;
    padding: 15px 20px;
    margin: 20px 0;
  }
  .totals-label {
    color: #888;
    font-size: 14px;
  }

  .closed-notes-toggle {
    margin-top: 15px;
  }
  .closed-notes-hint {
    font-size: 13px;
    margin: 4px 0 0 22px;
  }

  .toggle-link {
    margin: 10px 0 4px 0;
  }
  .toggle-link a {
    color: #127285;
    cursor: pointer;
  }

  .note-list {
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 12px;
  }
  .note-list table {
    margin-bottom: 0;
  }

  .failures-block {
    margin-bottom: 15px;
  }
  .failure-list {
    max-height: 220px;
    overflow-y: auto;
    padding-left: 18px;
  }

  /* Destructive action: distinct from the teal cancel and green accept so an
     admin cannot confuse "cancel this request" with "close this dialog". */
  .modal-wrapper .btn-discard {
    background-color: #f05050;
    border-color: #f05050;
    color: #ffffff;
    transition: all .4s ease-in-out;
  }
  .modal-wrapper .btn-discard:hover {
    background-color: #d33f3f;
    border-color: #d33f3f;
  }
</style>



// WEBPACK FOOTER //
// BulkTransfer.vue?58ca3e51